import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Supplier } from './suppliers.model';
import { Page } from '../../models/page-wrapper.model';
import { environment } from '../../../environments/environment';
import { PickerRecentCacheService } from '../../shared/services/picker-recent-cache.service';
import { EntityDropdownPickerAdapter } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { PickerRecentRecord } from '../../shared/components/entity-dropdown-picker/picker-recent.model';

@Injectable({
  providedIn: 'root'
})
export class SuppliersService {
  private apiUrl = `${environment.restApiUrl}masterdata/suppliers`;
  private readonly pickerEntityKey = 'supplier';

  constructor(
    private http: HttpClient,
    private recentPickerCache: PickerRecentCacheService
  ) { }

  private handleError(error: HttpErrorResponse) {
    // A client-side or network error occurred.
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${JSON.stringify(error.error)}`
      );
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something went wrong; please try again later.'));
  }

  // getAllSuppliers(params?: any): Observable<Page<Supplier>> {
  //   return this.http.get<Page<Supplier>>(this.apiUrl, { params }).pipe(catchError(this.handleError));
  // }
  
  getAllSuppliers(params?: any): Observable<Page<Supplier>> {
    return this.http.get<Page<Supplier>>(this.apiUrl + '/search', { params:params }).pipe(catchError(this.handleError));
  }

  getSupplierById(id: number): Observable<Supplier> {
    return this.http.get<Supplier>(`${this.apiUrl}/${id}`).pipe(catchError(this.handleError));
  }

  searchSuppliers(query: string, page = 0, size = 25): Observable<Page<Supplier>> {
    return forkJoin([
      this.searchSuppliersByField('supplierName', query, page, size),
      this.searchSuppliersByField('supplierNumber', query, page, size),
    ]).pipe(
      map(([namePage, numberPage]) => this.mergeSupplierPages(namePage, numberPage, page, size)),
      catchError(this.handleError)
    );
  }

  loadRecentSuppliers(limit = 10): Observable<Supplier[]> {
    return from(this.recentPickerCache.loadRecent<Supplier>(this.pickerEntityKey, limit));
  }

  getFreshSupplierById(id: number | string): Observable<Supplier> {
    return this.getSupplierById(Number(id));
  }

  rememberSupplier(supplier: Supplier): Observable<void> {
    const record: PickerRecentRecord<Supplier> = {
      key: `${this.pickerEntityKey}::${supplier.id}`,
      entityKey: this.pickerEntityKey,
      userKey: this.getCurrentUserKey(),
      entityId: supplier.id,
      label: `${supplier.supplierName ?? ''}${supplier.supplierNumber ? ` - ${supplier.supplierNumber}` : ''}`.trim(),
      payload: supplier,
      lastUsedAt: Date.now(),
    };

    return from(this.recentPickerCache.remember(record));
  }

  createSupplierPickerAdapter(): EntityDropdownPickerAdapter<Supplier> {
    return {
      entityKey: this.pickerEntityKey,
      loadRecent: (limit?: number) => this.loadRecentSuppliers(limit),
      search: (query: string, page: number, size: number) => this.searchSuppliers(query, page, size),
      getFreshById: (id: number | string) => this.getFreshSupplierById(id),
      rememberSelection: (supplier: Supplier) => this.rememberSupplier(supplier),
      getId: (supplier: Supplier) => supplier.id,
      getLabel: (supplier: Supplier) => `${supplier.supplierName ?? supplier.supplierNumber ?? supplier.id ?? ''}${supplier.supplierNumber && supplier.supplierName ? ` - ${supplier.supplierNumber}` : ''}`.trim(),
      getSecondaryLabel: () => '',
      filterBy: 'supplierName,supplierNumber',
    };
  }

  createSupplier(supplier: Partial<Supplier>): Observable<Supplier> {
    return this.http.post<Supplier>(this.apiUrl, supplier).pipe(catchError(this.handleError));
  }

  updateSupplier(supplier: Partial<Supplier>): Observable<Supplier> {
    return this.http
      .put<Supplier>(`${this.apiUrl}/${supplier.id}`, supplier)
      .pipe(catchError(this.handleError));
  }

  deleteSupplier(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(catchError(this.handleError));
  }

  private getCurrentUserKey(): string {
    try {
      const rawUser = sessionStorage.getItem('appUser');
      if (!rawUser) {
        return 'anonymous';
      }

      const parsed = JSON.parse(rawUser);
      return String(parsed?.id ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }

  private searchSuppliersByField(
    field: 'supplierName' | 'supplierNumber',
    query: string,
    page: number,
    size: number
  ): Observable<Page<Supplier>> {
    const params = {
      [field]: query,
      page,
      size,
    };

    return this.http.get<Page<Supplier>>(`${this.apiUrl}/search`, { params }).pipe(
      catchError(() => of(this.emptySupplierPage(page, size)))
    );
  }

  private mergeSupplierPages(
    firstPage: Page<Supplier>,
    secondPage: Page<Supplier>,
    page: number,
    size: number
  ): Page<Supplier> {
    const merged = new Map<number, Supplier>();
    [...(firstPage.content || []), ...(secondPage.content || [])].forEach((supplier) => {
      if (supplier?.id !== undefined && supplier?.id !== null) {
        merged.set(supplier.id, supplier);
      }
    });

    const content = Array.from(merged.values()).slice(0, size);

    return {
      ...firstPage,
      content,
      number: page,
      size,
      totalElements: merged.size,
      numberOfElements: content.length,
    };
  }

  private emptySupplierPage(page: number, size: number): Page<Supplier> {
    return {
      content: [],
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: page * size,
        paged: true,
        unpaged: false,
      },
      last: true,
      totalPages: 0,
      totalElements: 0,
      size,
      number: page,
      sort: { sorted: false, unsorted: true, empty: true },
      first: page === 0,
      numberOfElements: 0,
      empty: true,
    };
  }
}
